import { useState } from "react";
import { toast } from "sonner";
import { X, Upload, Play, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { BULK_CSV } from "@/constants/testIds";

// Round 17b · Bulk employee CSV import panel.
// The screen the operator uses to paste an Excel export or a Santus
// dump. Two-step contract mirrors the backend endpoint:
//   1. Preview (dry-run) — parses the CSV, checks duplicates against
//      the active master, returns a per-row envelope with row_number
//      (spreadsheet-eye counting, header = 1) and `action`.
//   2. Commit — only allowed after preview succeeded with zero invalid
//      rows. Refuses (400 `bulk_csv_invalid_rows`) if any row is
//      malformed; the whole batch is atomic.
const TEMPLATE = [
    "name,position,employee_number,emp_code,labour_rate_kina,dependants_declared,tax_status,travel_kina,housing_kina,other_kina,bus_kina,school_fee_kina,bank_bsb,bank_account,bank_title,bank_nickname,bank_lodgement_ref,active",
    "SMITH JOHN,LABOURER,0001,SMITH01,4.75,2,resident_decl,25.00,0,0,0,0,038-006,6001787079,SMITH JOHN,Main,,true",
    "KAPO MARY,STOREPERSON,0002,KAPO01,5.25,0,resident_decl,0,0,0,0,0,038-006,6001787080,KAPO MARY,Main,,true",
].join("\n");

const ACTION_STYLE = {
    create: { color: "var(--kp-success)", label: "Will create" },
    skip_duplicate: { color: "#B57800", label: "Skip · duplicate" },
    error: { color: "var(--kp-red)", label: "Error · fix row" },
};

export default function BulkImportPanel({ open, onClose, onImported }) {
    const [text, setText] = useState("");
    const [envelope, setEnvelope] = useState(null);
    const [busy, setBusy] = useState(false);

    if (!open) return null;

    const reset = () => { setText(""); setEnvelope(null); };
    const close = () => { reset(); onClose?.(); };

    const preview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/employees/bulk-import",
                { text, dry_run: true });
            setEnvelope(data);
            if (data.header_error) {
                toast.error(`Bad header — ${data.header_error}`);
            } else {
                const s = data.summary || {};
                toast.success(`Preview · ${s.valid || 0} valid, ${s.invalid || 0} error, ${s.will_skip || 0} duplicate`);
            }
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Preview failed");
        } finally {
            setBusy(false);
        }
    };

    const commit = async () => {
        if (!envelope || envelope.summary?.invalid > 0) {
            toast.error("Fix every row error before importing.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/employees/bulk-import",
                { text, dry_run: false });
            const n = (data.created || []).length;
            toast.success(`Imported ${n} employee${n === 1 ? "" : "s"}.`);
            onImported?.();
            close();
        } catch (ex) {
            const d = ex?.response?.data?.detail;
            if (d?.code === "bulk_csv_invalid_rows") {
                // Server refused because rows are malformed. Refresh
                // envelope so the row-numbered errors show.
                setEnvelope(d);
                toast.error(`${d.summary?.invalid || 0} row(s) invalid — fix and retry.`);
            } else {
                toast.error(formatApiError(d) || "Import failed");
            }
        } finally {
            setBusy(false);
        }
    };

    const rows = envelope?.rows || [];
    const summary = envelope?.summary || {};
    const canCommit = envelope && !envelope.header_error
        && summary.invalid === 0
        && (summary.will_create || 0) > 0;

    return (
        <div
            data-testid={BULK_CSV.panel}
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-8 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
            <aside className="bg-white w-full max-w-[900px] rounded-[3px] shadow-xl">
                <header className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">
                            Bulk import
                        </div>
                        <h2 className="font-display font-bold text-xl mt-0.5 text-[color:var(--kp-charcoal)]">
                            Employees from CSV
                        </h2>
                    </div>
                    <button
                        data-testid={BULK_CSV.closeBtn}
                        onClick={close}
                        className="p-1 text-neutral-400 hover:text-[color:var(--kp-charcoal)]"
                    >
                        <X size={18} />
                    </button>
                </header>

                <div className="p-6 space-y-4">
                    <div className="flex items-start gap-3 text-[12px] text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-[3px] px-4 py-3">
                        <FileText size={14} className="mt-0.5 shrink-0 text-neutral-400" />
                        <div className="flex-1">
                            Paste a CSV below. The first row must be the header. Preview parses without
                            writing; malformed rows show below with a spreadsheet row number so you can
                            find them in the source file. Nothing is created until you click Import.
                        </div>
                        <button
                            data-testid={BULK_CSV.templateBtn}
                            onClick={() => setText(TEMPLATE)}
                            className="text-[11px] font-display font-semibold uppercase tracking-[0.14em]"
                            style={{ color: "var(--kp-red)" }}
                        >
                            Load template
                        </button>
                    </div>

                    <textarea
                        data-testid={BULK_CSV.textarea}
                        value={text}
                        onChange={(e) => { setText(e.target.value); if (envelope) setEnvelope(null); }}
                        rows={9}
                        placeholder="name,position,employee_number,emp_code,labour_rate_kina,dependants_declared,tax_status,..."
                        className="kp-focus w-full border border-neutral-300 px-3 py-2 text-[13px] bg-white rounded-[3px] font-mono-money"
                    />

                    <div className="flex gap-2">
                        <button
                            data-testid={BULK_CSV.previewBtn}
                            onClick={preview}
                            disabled={busy || !text.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-[0.14em] border rounded-[3px]"
                            style={{ borderColor: "var(--kp-red)", color: "var(--kp-red)", opacity: busy || !text.trim() ? 0.5 : 1 }}
                        >
                            <Play size={12} /> Dry-run preview
                        </button>
                        <button
                            data-testid={BULK_CSV.commitBtn}
                            onClick={commit}
                            disabled={busy || !canCommit}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-[0.14em] text-white rounded-[3px]"
                            style={{ background: "var(--kp-red)", opacity: busy || !canCommit ? 0.4 : 1 }}
                        >
                            <Upload size={12} /> Import {summary.will_create || 0} valid row{(summary.will_create || 0) === 1 ? "" : "s"}
                        </button>
                    </div>

                    {envelope?.header_error && (
                        <div
                            data-testid={BULK_CSV.headerError}
                            className="border rounded-[3px] px-4 py-3 flex items-start gap-2 text-[13px]"
                            style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.06)", color: "var(--kp-red)" }}
                        >
                            <AlertTriangle size={14} className="mt-0.5" />
                            {envelope.header_error}
                        </div>
                    )}

                    {envelope && !envelope.header_error && (
                        <>
                            <div className="grid grid-cols-4 gap-2">
                                <SummaryChip label="Rows" value={summary.total || 0} testid={BULK_CSV.summaryTotal} />
                                <SummaryChip label="Will create" value={summary.will_create || 0} testid={BULK_CSV.summaryValid} tone="success" />
                                <SummaryChip label="Errors" value={summary.invalid || 0} testid={BULK_CSV.summaryInvalid} tone={summary.invalid ? "error" : ""} />
                                <SummaryChip label="Duplicates" value={summary.will_skip || 0} testid={BULK_CSV.summaryDup} tone={summary.will_skip ? "warn" : ""} />
                            </div>

                            <div data-testid={BULK_CSV.rowsTable} className="border border-neutral-200 rounded-[3px] overflow-hidden">
                                <table className="w-full text-[12px]">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                            <th className="px-3 py-2 w-[70px]">Row</th>
                                            <th className="px-3 py-2">Name</th>
                                            <th className="px-3 py-2">Emp code</th>
                                            <th className="px-3 py-2">Bank</th>
                                            <th className="px-3 py-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const st = ACTION_STYLE[r.action] || ACTION_STYLE.error;
                                            const parsed = r.parsed || {};
                                            const acct = (parsed.bank_accounts || [])[0];
                                            return (
                                                <tr
                                                    key={r.row_number}
                                                    data-testid={BULK_CSV.row(r.row_number)}
                                                    className="border-t border-neutral-100 align-top"
                                                    style={r.action === "error" ? { background: "rgba(227,6,19,0.03)" } : {}}
                                                >
                                                    <td className="px-3 py-2 font-mono-money text-neutral-500">{r.row_number}</td>
                                                    <td className="px-3 py-2 text-[color:var(--kp-charcoal)]">{parsed.name || <span className="text-neutral-400 italic">—</span>}</td>
                                                    <td className="px-3 py-2 font-mono-money text-neutral-600">{parsed.emp_code || "—"}</td>
                                                    <td className="px-3 py-2 font-mono-money text-neutral-600">
                                                        {acct ? `${acct.bsb} · ${acct.account_number}` : "—"}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div style={{ color: st.color }} className="font-display font-semibold text-[10px] uppercase tracking-[0.14em] flex items-center gap-1">
                                                            {r.action === "create" && <CheckCircle2 size={11} />}
                                                            {r.action === "error" && <AlertTriangle size={11} />}
                                                            {st.label}
                                                        </div>
                                                        {r.action === "error" && (r.errors || []).length > 0 && (
                                                            <ul data-testid={BULK_CSV.rowErrors(r.row_number)} className="mt-1 text-[11px] text-[color:var(--kp-red)] list-disc pl-4">
                                                                {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                                                            </ul>
                                                        )}
                                                        {r.action === "skip_duplicate" && (
                                                            <div className="text-[11px] text-neutral-500 mt-0.5">{r.reason}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </aside>
        </div>
    );
}

function SummaryChip({ label, value, testid, tone }) {
    const color = tone === "success" ? "var(--kp-success)"
                : tone === "error" ? "var(--kp-red)"
                : tone === "warn" ? "#B57800"
                : "var(--kp-charcoal)";
    return (
        <div className="border border-neutral-200 rounded-[3px] p-2 bg-neutral-50">
            <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display">{label}</div>
            <div data-testid={testid} className="font-mono-money text-[15px] mt-0.5 font-semibold" style={{ color }}>{value}</div>
        </div>
    );
}
